"use client";

/**
 * "Add to your home screen" for saved plans on mobile: an action-bar button,
 * plus a card shown once the visitor has put their plan in a calendar.
 * The home-screen icon opens the saved plan (`/plan-your-visit/[planId]`) as
 * a standalone browser app.
 *
 *  - Chromium on Android fires `beforeinstallprompt`; we hold on to it and
 *    install in one tap.
 *  - iOS has no install API, so we show the Share → Add to Home Screen steps.
 *    Safari reads the manifest only on page load, so when the plan URL was set
 *    in place (the planner modal) we reload it first.
 *  - Other mobile browsers get the browser-menu instructions.
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect, useSyncExternalStore } from "react";
import { EllipsisVertical, Share, Smartphone, X } from "lucide-react";
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
  }>;
}
export type HomeScreenPlatform = "prompt" | "ios" | "other";
const DISMISSED_KEY = "va-plan-home-screen-dismissed";
const RELOAD_PARAM = "home-screen";
let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
function notify() {
  for (const listener of listeners) listener();
}
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", event => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) || /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
}
function isMobile(): boolean {
  return isIos() || /Android|Mobi/i.test(navigator.userAgent);
}
function isStandalone(): boolean {
  return window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & {
    standalone?: boolean;
  }).standalone === true;
}
function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}
function writeDismissed() {
  try {
    window.localStorage.setItem(DISMISSED_KEY, "1");
  } catch {
    return;
  }
}
function getPlatform(): HomeScreenPlatform | null {
  if (!isMobile() || isStandalone()) return null;
  if (deferredPrompt) return "prompt";
  return isIos() ? "ios" : "other";
}

/** Opens the browser's install dialog; resolves `true` when accepted. */
export async function promptInstall(): Promise<boolean> {
  const event = deferredPrompt;
  if (!event) return false;
  await event.prompt();
  const {
    outcome
  } = await event.userChoice;
  deferredPrompt = null;
  notify();
  return outcome === "accepted";
}

/** Whether the page was loaded at the current URL rather than moved there in place. */
export function loadedAtCurrentUrl(): boolean {
  const [entry] = performance.getEntriesByType?.("navigation") ?? [];
  return !!entry && new URL(entry.name).pathname === window.location.pathname;
}

/** Reloads the current URL, then `takeHomeScreenReload` reopens the steps. */
export function reloadForHomeScreen() {
  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_PARAM, "1");
  window.location.assign(url);
}

/** Whether this load came from `reloadForHomeScreen`; drops the marker from the URL. */
export function takeHomeScreenReload(): boolean {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(RELOAD_PARAM)) return false;
  url.searchParams.delete(RELOAD_PARAM);
  window.history.replaceState(window.history.state, "", url);
  return true;
}

/** Puts the plan's manifest first in <head> so an install opens that plan. */
export function usePlanManifest(planId, locale) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== locale || $[1] !== planId) {
    t0 = () => {
      if (!planId) {
        return;
      }
      const link = document.createElement("link");
      link.rel = "manifest";
      link.crossOrigin = "use-credentials";
      link.href = `/api/concierge/plan/${planId}/manifest?locale=${encodeURIComponent(locale)}`;
      document.head.prepend(link);
      return () => link.remove();
    };
    t1 = [planId, locale];
    $[0] = locale;
    $[1] = planId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}

/** Where the prompt can show on this device, or `null` when it shouldn't. */
export function useHomeScreenPlatform() {
  return useSyncExternalStore(subscribe, getPlatform, _temp);
}

/** Whether to show the card after a calendar add on this device. */
function _temp() {
  return null;
}
export function shouldOfferHomeScreen(): boolean {
  return getPlatform() !== null && !readDismissed();
}
export function AddToHomeScreen(t0) {
  const $ = _c(19);
  const {
    platform,
    onClose,
    onInstalled
  } = t0;
  let t1;
  if ($[0] !== onClose) {
    t1 = function dismiss() {
      writeDismissed();
      onClose();
    };
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const dismiss = t1;
  const needsReload = platform !== "prompt" && !loadedAtCurrentUrl();
  let t2;
  if ($[2] !== needsReload || $[3] !== onClose || $[4] !== onInstalled) {
    t2 = async function install() {
      if (needsReload) {
        reloadForHomeScreen();
        return;
      }
      if (await promptInstall()) {
        onInstalled?.();
        onClose();
      }
    };
    $[2] = needsReload;
    $[3] = onClose;
    $[4] = onInstalled;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const install = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="grid size-9 place-items-center rounded-full bg-sunshine-yellow-400/15 text-sunshine-yellow-400"><Smartphone size={16} aria-hidden={true} /></span>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-sunshine-yellow-400">Keep your plan handy</p>;
    t5 = <p className="typography-body-xs-regular text-cream-100/80">Add it to your home screen so your day is always one tap away.</p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== install || $[10] !== needsReload || $[11] !== platform) {
    t6 = <div className="min-w-0">{t4}{t5}{platform === "prompt" || needsReload ? <button type="button" onClick={() => void install()} className="mt-3 inline-flex items-center gap-2 rounded-full bg-sunshine-yellow-400 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-deep-sea-blue-950 hover:brightness-105"><Smartphone size={14} aria-hidden={true} /> Add to home screen</button> : <ol className="typography-body-xs-regular mt-3 grid gap-1.5 text-cream-100/80"><li className="flex items-center gap-1.5"><span className="text-sunshine-yellow-400">1.</span>{platform === "ios" ? <>Tap{" "}<Share size={14} aria-label="Share" className="text-sunshine-yellow-400" />{" "}in your browser</> : <>Open your browser menu{" "}<EllipsisVertical size={14} aria-label="menu" className="text-sunshine-yellow-400" /></>}</li><li className="flex items-center gap-1.5"><span className="text-sunshine-yellow-400">2.</span>Choose “Add to Home Screen”</li></ol>}</div>;
    $[9] = install;
    $[10] = needsReload;
    $[11] = platform;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <X size={16} aria-hidden={true} />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== dismiss) {
    t8 = <button type="button" onClick={dismiss} aria-label="Dismiss" className="grid size-8 place-items-center rounded-full text-cream-100/60 hover:text-cream-100">{t7}</button>;
    $[14] = dismiss;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t6 || $[17] !== t8) {
    t9 = <div role="region" aria-label="Add your plan to your home screen" className="mt-4 grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 rounded-2xl border border-sunshine-yellow-400/30 bg-deep-sea-blue-900/80 p-4 text-cream-100 backdrop-blur">{t3}{t6}{t8}</div>;
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}